import React, { useState, useEffect, useRef } from 'react';
import { WebsiteDesignProps } from './types';
import { 
  LayoutGrid, Calendar, Users, Briefcase, ArrowUpRight, ArrowRight,
  Menu, X, Zap, Heart, Trophy, School, Clock, 
  MapPin, Phone, Mail, Globe, GraduationCap, ChevronLeft, 
  ChevronRight, ChevronDown, Download, Send, Plus, Layout, Sparkles, 
  FileText, Instagram, Facebook, Linkedin, Youtube, Quote, BookOpen, Star, Camera
} from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { Button } from "@/components/ui/button";
import { VisualImageEditable } from "@/components/VisualImageEditable";
import { VisualTextEditable } from "@/components/VisualTextEditable";

const CompactBento: React.FC<WebsiteDesignProps> = ({ previewData, isEditMode, onUpdateField }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [showLoginDropdown, setShowLoginDropdown] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Icon resolver helper
  const getIcon = (name: string | any) => {
      if (!name) return LayoutGrid;
      if (typeof name !== 'string') return name;
      // @ts-ignore
      const Icon = LucideIcons[name];
      return Icon || LayoutGrid;
  };

  const schoolName = previewData?.name || "City International Academy";

  // Shared Data Model (Strict Matching Design 1)
  const defaultData = {
    hero: {
      images: ["https://images.unsplash.com/photo-1523050854058-8df90110c9f1?q=80&w=2070&auto=format&fit=crop"],
      tagline: "Admissions Open 2026/27",
      titlePrefix: "Shaping Leaders",
      subtitle: "Experience a modular approach to world-class education where curiosity meets excellence in the heart of the city."
    },
    features: [
      { icon: "Star", title: "Top Ranked", desc: "Consistently rated #1 for academic excellence in the region." },
      { icon: "Users", title: "Global Community", desc: "Over 50 nationalities represented in our student body." },
      { icon: "Trophy", title: "Sports Elite", desc: "State-of-the-art facilities for over 15 competitive sports." }
    ],
    programs: [
      { title: "Primary Years", age: "Years 1-6", desc: "Foundational mastery through inquiry-based learning.", img: "https://images.unsplash.com/photo-1503676260728-1c00da094a0b?q=80&w=2022&auto=format&fit=crop" },
      { title: "Senior College", age: "Years 10-13", desc: "Advanced placement and university preparation pathways.", img: "https://images.unsplash.com/photo-1523240795612-9a054b0db644?q=80&w=2070&auto=format&fit=crop" }
    ],
    stats: [
        { value: "100%", label: "Univ Placement" },
        { value: "15:1", label: "Student Ratio" },
        { value: "45+", label: "ECAs Offered" }
    ],
    news: [
        { title: "Campus Extension", date: "JAN 15", desc: "Unveiling our new sustainable arts wing next month.", img: "https://images.unsplash.com/photo-1497366216548-37526070297c?q=80&w=2069&auto=format&fit=crop" }
    ],
    nav: [
        { label: 'Campus', href: '#features' },
        { label: 'Programs', href: '#programs' },
        { label: 'Facilities', href: '#facilities' },
        { label: 'News', href: '#news' }
    ],
    team: [
        { name: "Dr. Elena Rossi", role: "Principal", img: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?q=80&w=1976&auto=format&fit=crop" },
        { name: "Mark Thompson", role: "Academic Dean", img: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?q=80&w=2070&auto=format&fit=crop" }
    ],
    gallery: [
        { category: "Campus", img: "https://images.unsplash.com/photo-1541339907198-e08756ebafe3?q=80&w=2070&auto=format&fit=crop", title: "Main Hall" },
        { category: "Lab", img: "https://images.unsplash.com/photo-1532094349884-543bb1198343?q=80&w=2070&auto=format&fit=crop", title: "Science Hub" }
    ],
    facilities: [
        { title: "Digital Library", desc: "Over 50,000 resources and quiet study zones.", img: "https://images.unsplash.com/photo-1507842217343-583bb7270b66?q=80&w=2000&auto=format&fit=crop", icon: "BookOpen" },
        { title: "Olympic Pool", desc: "Heated indoor aquatic center for all seasons.", img: "https://images.unsplash.com/photo-1519739023199-31e8992f4949?q=80&w=2070&auto=format&fit=crop", icon: "Trophy" }
    ]
  };

  const mockData = {
      ...previewData,
      hero: {
          titlePrefix: previewData?.hero?.titlePrefix || defaultData.hero.titlePrefix,
          tagline: previewData?.hero?.tagline || defaultData.hero.tagline,
          subtitle: previewData?.hero?.subtitle || defaultData.hero.subtitle,
          images: previewData?.hero?.images || defaultData.hero.images,
          backgroundImage: previewData?.hero?.backgroundImage || defaultData.hero.images[0]
      },
      about: {
          title: previewData?.about?.title || "Excellence in Every Block.",
          description: previewData?.about?.description || "City International Academy provides a modern, fast-paced environment where student success is built through a modular, comprehensive curriculum.",
          sectionImage: previewData?.about?.sectionImage || "https://images.unsplash.com/photo-1523050854058-8df90110c9f1?q=80&w=2070&auto=format&fit=crop"
      },
      features: (previewData?.features || defaultData.features).map((f: any) => ({ 
        ...f, 
        icon: getIcon(f.icon),
        desc: f.description || f.desc || ""
      })),
      programs: (previewData?.programs || defaultData.programs).map((p: any) => ({
        title: p.name || p.title || "",
        age: p.age || "",
        desc: p.description || p.desc || "",
        img: p.image || p.img || ""
      })),
      stats: (previewData?.stats || defaultData.stats).map((s: any) => ({
          value: s.value || "0",
          label: s.label || ""
      })),
      news: (previewData?.news || defaultData.news).map((n: any) => ({
        title: n.title || "",
        date: n.date || "",
        desc: n.description || n.desc || "",
        img: n.image || n.img || ""
      })),
      team: (previewData?.team || defaultData.team).map((t: any) => ({
        name: t.name || "",
        role: t.role || "",
        img: t.img || t.image || "",
        linkedin: t.linkedin || "",
        email: t.email || ""
      })),
      gallery: (previewData?.gallery || defaultData.gallery).map((g: any) => ({
        category: g.category || "General",
        title: g.caption || g.title || "",
        img: g.image || g.img || ""
      })),
      facilities: (previewData?.facilities || defaultData.facilities).map((f: any) => ({ 
        ...f, 
        icon: getIcon(f.icon),
        desc: f.description || f.desc || "",
        img: f.image || f.img || ""
      })),
      downloads: (previewData?.downloads || [
          { title: "Academic_Prospectus.pdf", size: "3.5 MB", type: "PDF" },
          { title: "Fee_Structure_2026.pdf", size: "0.8 MB", type: "PDF" }
      ]).map((d: any) => ({
          title: d.title || "",
          size: d.size || "",
          type: d.type || "PDF"
      })),
      contact: {
        address: previewData?.contact?.address || "12 Academy Boulevard, Nairobi, Kenya",
        phone: previewData?.contact?.phone || "+254 700 111 222",
        email: previewData?.contact?.email || "admissions@cityint.ac.ke",
        officeHours: previewData?.contact?.officeHours || "Mon - Fri: 7:30 AM - 5:00 PM",
        facebook: previewData?.contact?.facebook || "",
        instagram: previewData?.contact?.instagram || "",
        youtube: previewData?.contact?.youtube || "",
        linkedin: previewData?.contact?.linkedin || ""
      },
      sectionVisibility: {
          hero: previewData?.sectionVisibility?.hero !== false,
          about: previewData?.sectionVisibility?.about !== false,
          stats: previewData?.sectionVisibility?.stats !== false,
          features: previewData?.sectionVisibility?.features !== false,
          programs: previewData?.sectionVisibility?.programs !== false,
          facilities: previewData?.sectionVisibility?.facilities !== false,
          team: previewData?.sectionVisibility?.team !== false,
          gallery: previewData?.sectionVisibility?.gallery !== false,
          news: previewData?.sectionVisibility?.news !== false,
          downloads: previewData?.sectionVisibility?.downloads !== false,
          cta: previewData?.sectionVisibility?.cta !== false,
          contact: previewData?.sectionVisibility?.contact !== false
      },
      nav: (previewData?.nav || defaultData.nav).map((n: any) => ({
          label: n.label || "",
          href: n.href || "#",
          visible: n.visible !== false
      })),
      labels: {
          downloadsSection: previewData?.labels?.downloadsSection || "Quick Resources",
          downloadsTitle: previewData?.labels?.downloadsTitle || "Essential Documents",
          contactFormTitle: previewData?.labels?.contactFormTitle || "Contact Form",
          contactFormSubtitle: previewData?.labels?.contactFormSubtitle || "Fill out the form below."
      }
  };

  useEffect(() => {
    const doc = containerRef.current?.ownerDocument || document;
    const root = doc.documentElement;
    if (previewData?.theme) {
        const { colors, fonts } = previewData.theme;
        root.style.setProperty('--color-primary', colors.primary || '#3b82f6');
        root.style.setProperty('--color-secondary', colors.secondary || '#1e293b');
        root.style.setProperty('--color-accent', colors.accent || '#f59e0b');
        root.style.setProperty('--color-background', colors.background || '#f3f4f6');
        root.style.setProperty('--color-text', colors.text || '#0f172a');
        root.style.setProperty('--font-heading', `'${fonts.heading || 'Inter'}', sans-serif`);
        root.style.setProperty('--font-body', `'${fonts.body || 'Plus Jakarta Sans'}', sans-serif`);
        const radiusMap = { none: '0px', small: '12px', medium: '24px', large: '32px', full: '9999px' };
        root.style.setProperty('--radius', radiusMap[previewData.theme.borderRadius || 'medium']);
    }
    const fontLink = doc.createElement('link');
    fontLink.href = "https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800&family=Inter:wght@400;700;900&display=swap";
    fontLink.rel = 'stylesheet';
    doc.head.appendChild(fontLink);
  }, [previewData?.theme]);

  const handleUpdate = (path: string, val: any) => onUpdateField && onUpdateField(path, val);
  const addArrayItem = (path: string, newItem: any) => {
    if (onUpdateField) {
        const arr = [...((mockData as any)[path] || [])];
        onUpdateField(path, [...arr, newItem]);
    }
  };
  const removeArrayItem = (path: string, index: number) => {
    if (onUpdateField && window.confirm('Remove this block from the grid?')) {
        const arr = [...((mockData as any)[path] || [])];
        onUpdateField(path, arr.filter((_, i) => i !== index));
    }
  };

  return (
    <div ref={containerRef} className="compact-bento bg-[var(--color-background)] text-[var(--color-text)] min-h-screen font-[var(--font-body)] p-4 md:p-8 selection:bg-[var(--color-primary)] selection:text-white">
      
      {/* Bento Navigation */}
      <nav className="max-w-7xl mx-auto flex justify-between items-center mb-6 px-4 bg-white/60 backdrop-blur-xl rounded-[var(--radius)] h-20 shadow-sm border border-white/40">
          <div className="flex items-center gap-4">
              <VisualImageEditable 
                  src={previewData?.logoUrl || ""} 
                  alt="Logo" 
                  containerClassName="w-10 h-10 flex items-center justify-center bg-white rounded-xl shadow-sm p-1"
                  className="w-full h-full object-contain"
                  onImageChange={(u) => handleUpdate('logoUrl', u)}
                  isEditMode={!!isEditMode}
                  fallback={<div className="w-full h-full bg-[var(--color-primary)] rounded-lg flex items-center justify-center text-white"><School size={20}/></div>}
              />
              <span className="font-extrabold text-xl tracking-tighter uppercase font-[var(--font-heading)]">
                  <VisualTextEditable value={schoolName} onSave={(v) => handleUpdate('name', v)} isEditMode={!!isEditMode} />
              </span>
          </div>
          
          <div className="hidden lg:flex items-center gap-8">
              <div className="flex gap-8 text-[12px] font-bold uppercase tracking-widest text-[var(--color-text)]/60">
                  {mockData.nav.filter(n => n.visible).map((n, i) => (
                      <a key={i} href={n.href} className="hover:text-[var(--color-primary)] transition-colors">
                          {n.label}
                      </a>
                  ))}
              </div>
              <div className="h-8 w-[1px] bg-[var(--color-text)]/10" />
              <button onClick={() => setShowLoginDropdown(!showLoginDropdown)} className="bg-[var(--color-secondary)] text-white px-6 py-2 rounded-full text-[10px] font-bold uppercase tracking-widest flex items-center gap-2 hover:bg-[var(--color-primary)] transition-all">
                  Portal <ChevronDown size={14} className={showLoginDropdown ? 'rotate-180' : ''} />
              </button>
          </div>

          <button className="lg:hidden" onClick={() => setMenuOpen(!menuOpen)}>
              {menuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
      </nav>

      {/* Main Bento Grid */}
      <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-4 grid-rows-auto gap-4 md:gap-6">
          
          {/* Hero Block (Main Feature) */}
          {mockData.sectionVisibility.hero && (
          <div className="col-span-1 md:col-span-2 md:row-span-2 bg-white rounded-[var(--radius)] p-10 flex flex-col justify-between shadow-sm border border-white relative overflow-hidden group min-h-[500px]">
              <div className="relative z-10">
                  <span className="inline-flex items-center gap-2 bg-blue-50 text-[var(--color-primary)] px-4 py-1.5 rounded-full text-[10px] font-bold mb-8 uppercase tracking-[0.2em]">
                      <Sparkles size={14} className="animate-pulse" />
                      <VisualTextEditable value={mockData.hero.tagline} onSave={(v) => handleUpdate('hero.tagline', v)} isEditMode={!!isEditMode} />
                  </span>
                  <h1 className="text-5xl md:text-7xl font-extrabold font-[var(--font-heading)] leading-[0.9] mb-8 tracking-tighter">
                      <VisualTextEditable value={mockData.hero.titlePrefix} onSave={(v) => handleUpdate('hero.titlePrefix', v)} isEditMode={!!isEditMode} /><br/>
                      <span className="text-[var(--color-primary)]">Modern Minds.</span>
                  </h1>
                  <p className="text-gray-500 text-lg leading-relaxed max-w-sm mb-10">
                      <VisualTextEditable value={mockData.hero.subtitle} onSave={(v) => handleUpdate('hero.subtitle', v)} isEditMode={!!isEditMode} multiline />
                  </p>
                  <Button className="bg-[var(--color-primary)] text-white h-14 px-8 rounded-2xl text-xs font-bold uppercase tracking-widest hover:scale-105 transition-transform flex items-center gap-3">
                      Start Application <ArrowUpRight size={18} />
                  </Button>
              </div>
              <div className="absolute right-[-10%] bottom-[-10%] w-3/4 aspect-square">
                 <VisualImageEditable 
                    src={mockData.hero.backgroundImage} 
                    className="w-full h-full object-cover rounded-tl-[100px] grayscale hover:grayscale-0 transition-all duration-700"
                    onImageChange={(u) => handleUpdate('hero.backgroundImage', u)}
                    isEditMode={!!isEditMode}
                    alt="Hero"
                 />
              </div>
          </div>
          )}

          {/* Stats Blocks (Mini grid in grid) */}
          {mockData.sectionVisibility.stats && (
          <div className="col-span-1 grid grid-cols-1 gap-4 md:gap-6">
              {mockData.stats.map((stat, i) => (
                  <div key={i} className={`rounded-[var(--radius)] p-8 flex flex-col justify-center shadow-sm border border-white ${i % 2 === 0 ? 'bg-[var(--color-secondary)] text-white' : 'bg-white text-[var(--color-text)]'}`}>
                      <div className={`text-4xl md:text-5xl font-black mb-1 ${i % 2 === 0 ? 'text-[var(--color-primary)]' : 'text-[var(--color-secondary)]'}`}>
                        <VisualTextEditable value={stat.value} onSave={(v) => handleUpdate(`stats[${i}].value`, v)} isEditMode={!!isEditMode} />
                      </div>
                      <div className="text-[10px] font-bold uppercase tracking-[0.3em] opacity-60">
                        <VisualTextEditable value={stat.label} onSave={(v) => handleUpdate(`stats[${i}].label`, v)} isEditMode={!!isEditMode} />
                      </div>
                  </div>
              ))}
              {isEditMode && (
                  <button onClick={() => addArrayItem('stats', { value: "New", label: "Value" })} className="p-6 border-2 border-dashed border-gray-300 rounded-[var(--radius)] text-gray-400 hover:text-[var(--color-primary)] hover:border-[var(--color-primary)] transition-all">Add Stat</button>
              )}
          </div>
          )}

          {/* Image Block / Preview */}
          <div className="col-span-1 md:row-span-2 bg-slate-200 rounded-[var(--radius)] overflow-hidden relative group shadow-sm min-h-[400px]">
               <VisualImageEditable 
                  src={mockData.about.sectionImage} 
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-1000" 
                  alt="About Section"
                  onImageChange={(u) => handleUpdate('about.sectionImage', u)}
                  isEditMode={!!isEditMode}
               />
               <div className="absolute inset-x-4 bottom-4 p-6 bg-white/70 backdrop-blur-md rounded-3xl border border-white/30">
                   <h3 className="font-extrabold text-xl font-[var(--font-heading)] leading-none text-[var(--color-secondary)] mb-2">Our Campus Oasis.</h3>
                   <p className="text-xs text-[var(--color-secondary)]/60 font-medium">Click to explore facilities.</p>
               </div>
          </div>

          {/* About Block - The Philosophy */}
          {mockData.sectionVisibility.about && (
          <div className="col-span-1 md:col-span-2 bg-white rounded-[var(--radius)] p-10 shadow-sm border border-white flex flex-col justify-center">
              <h3 className="text-3xl font-black font-[var(--font-heading)] italic uppercase tracking-tighter mb-6">
                <VisualTextEditable value={mockData.about.title} onSave={(v) => handleUpdate('about.title', v)} isEditMode={!!isEditMode} />
              </h3>
              <p className="text-gray-500 font-medium leading-relaxed italic">
                <VisualTextEditable value={mockData.about.description} onSave={(v) => handleUpdate('about.description', v)} isEditMode={!!isEditMode} multiline />
              </p>
          </div>
          )}

          {/* Team Block - Leadership */}
          {mockData.sectionVisibility.team && (
          <div className="col-span-1 md:col-span-2 bg-[var(--color-primary)]/5 rounded-[var(--radius)] p-8 shadow-sm border border-[var(--color-primary)]/10">
              <div className="flex justify-between items-center mb-8">
                  <h3 className="font-black uppercase tracking-widest text-xs">Faculty Nodes</h3>
                  <Users size={20} className="text-[var(--color-primary)]" />
              </div>
              <div className="grid grid-cols-2 gap-6">
                  {mockData.team.map((m, i) => (
                      <div key={i} className="flex items-center gap-4 group">
                          <div className="w-14 h-14 rounded-2xl overflow-hidden grayscale group-hover:grayscale-0 transition-all">
                               <VisualImageEditable 
                                  src={m.img} 
                                  alt={m.name} 
                                  className="w-full h-full object-cover" 
                                  onImageChange={(u) => handleUpdate(`team[${i}].img`, u)} 
                                  isEditMode={!!isEditMode}
                              />
                          </div>
                          <div>
                              <h4 className="text-[10px] font-black uppercase leading-tight">
                                <VisualTextEditable value={m.name} onSave={(v) => handleUpdate(`team[${i}].name`, v)} isEditMode={!!isEditMode} />
                              </h4>
                              <p className="text-[8px] font-bold text-[var(--color-primary)] uppercase tracking-widest mt-1">
                                <VisualTextEditable value={m.role} onSave={(v) => handleUpdate(`team[${i}].role`, v)} isEditMode={!!isEditMode} />
                              </p>
                          </div>
                          {isEditMode && <button onClick={() => removeArrayItem('team', i)} className="text-[8px] text-red-500 font-bold ml-auto opacity-0 group-hover:opacity-100">X</button>}
                      </div>
                  ))}
                  {isEditMode && (
                      <div onClick={() => addArrayItem('team', { name: "NEW", role: "FACULTY", img: "" })} className="flex items-center justify-center p-4 border-2 border-dashed border-[var(--color-primary)]/20 rounded-2xl cursor-pointer hover:bg-white">
                          <Plus size={16} />
                      </div>
                  )}
              </div>
          </div>
          )}

          {/* Gallery Block - Visual Matrix */}
          {mockData.sectionVisibility.gallery && (
          <div className="col-span-1 md:col-span-2 grid grid-cols-2 gap-4">
              {mockData.gallery.map((item, i) => (
                  <div key={i} className="aspect-square rounded-[var(--radius)] overflow-hidden relative group">
                      <VisualImageEditable 
                          src={item.img} alt={item.title}
                          className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all"
                          onImageChange={(u) => handleUpdate(`gallery[${i}].img`, u)}
                          isEditMode={!!isEditMode}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity p-4 flex flex-col justify-end text-white">
                          <p className="text-[8px] font-black uppercase tracking-widest">{item.category}</p>
                          <h4 className="text-xs font-bold truncate">{item.title}</h4>
                      </div>
                      {isEditMode && <button onClick={() => removeArrayItem('gallery', i)} className="absolute top-2 right-2 text-[8px] font-bold text-white bg-red-500 rounded-full w-4 h-4 flex items-center justify-center opacity-0 group-hover:opacity-100">X</button>}
                  </div>
              ))}
              {isEditMode && (
                  <div onClick={() => addArrayItem('gallery', { category: "CAMPUS", title: "NEW", img: "" })} className="aspect-square border-2 border-dashed border-gray-200 rounded-[var(--radius)] flex items-center justify-center cursor-pointer hover:bg-white text-gray-300">
                      <Camera size={24} />
                  </div>
              )}
          </div>
          )}

          {/* Facilities Block - Infrastructure */}
          {mockData.sectionVisibility.facilities && (
          <div className="col-span-1 md:col-span-2 bg-white rounded-[var(--radius)] p-10 shadow-sm border border-white relative overflow-hidden group">
              <div className="flex items-center gap-6 mb-8">
                   <div className="w-12 h-12 bg-orange-50 text-orange-500 rounded-2xl flex items-center justify-center"><LayoutGrid size={24} /></div>
                   <h3 className="text-2xl font-black font-[var(--font-heading)] uppercase italic tracking-tighter text-[var(--color-secondary)]">The Infrastructure.</h3>
              </div>
              <div className="space-y-6">
                  {mockData.facilities.map((fac, i) => (
                      <div key={i} className="flex gap-6 items-start group/fac">
                          <div className="w-20 h-20 rounded-2xl overflow-hidden shrink-0 border border-gray-100 shadow-sm">
                               <VisualImageEditable 
                                  src={fac.img} alt={fac.title} 
                                  className="w-full h-full object-cover" 
                                  onImageChange={(u) => handleUpdate(`facilities[${i}].img`, u)} 
                                  isEditMode={!!isEditMode}
                              />
                          </div>
                          <div>
                              <h4 className="font-extrabold text-sm mb-1 uppercase italic tracking-tight flex items-center gap-3">
                                <VisualTextEditable value={fac.title} onSave={(v) => handleUpdate(`facilities[${i}].title`, v)} isEditMode={!!isEditMode} />
                                {React.createElement(fac.icon, { size: 14, className: "text-orange-500" })}
                              </h4>
                              <p className="text-xs text-gray-400 font-medium leading-relaxed">
                                <VisualTextEditable value={fac.desc} onSave={(v) => handleUpdate(`facilities[${i}].desc`, v)} isEditMode={!!isEditMode} multiline />
                              </p>
                          </div>
                          {isEditMode && <button onClick={() => removeArrayItem('facilities', i)} className="text-[8px] text-red-500 font-bold opacity-0 group-hover/fac:opacity-100">Drop</button>}
                      </div>
                  ))}
                  {isEditMode && <button onClick={() => addArrayItem('facilities', { title: "NEW", desc: "DESC", icon: "Layout" })} className="text-[8px] font-black uppercase text-orange-500 underline">Sync Node</button>}
              </div>
          </div>
          )}

          {/* News Banner */}
          {mockData.sectionVisibility.news && (
          <div className="col-span-1 md:col-span-2 bg-[var(--color-accent)] rounded-[var(--radius)] p-8 shadow-sm border border-white/20 flex flex-col md:flex-row gap-8 items-center cursor-pointer hover:brightness-105 transition-all">
              <div className="w-24 h-24 shrink-0 rounded-2xl overflow-hidden border-2 border-white/40">
                  <VisualImageEditable 
                    src={mockData.news[0]?.img || ""} 
                    className="w-full h-full object-cover" 
                    onImageChange={(u) => handleUpdate(`news[0].img`, u)}
                    isEditMode={!!isEditMode}
                    alt="Latest News"
                  />
              </div>
              <div className="text-center md:text-left text-white">
                  <span className="text-[10px] font-bold uppercase tracking-widest opacity-60 mb-2 block">LATEST_DISPATCH // {mockData.news[0]?.date}</span>
                  <h3 className="text-2xl font-black font-[var(--font-heading)] leading-none mb-4 italic uppercase">
                    <VisualTextEditable value={mockData.news[0]?.title || ""} onSave={(v) => handleUpdate(`news[0].title`, v)} isEditMode={!!isEditMode} />
                  </h3>
                  <Button variant="ghost" className="text-white hover:bg-white/20 p-0 h-auto text-[10px] font-bold uppercase tracking-widest">Read Signal <ArrowRight size={14} className="ml-2"/></Button>
              </div>
          </div>
          )}

          {/* Downloads / CTA Box */}
          {mockData.sectionVisibility.downloads && (
          <div className="col-span-1 bg-white rounded-[var(--radius)] p-8 shadow-sm border border-white flex flex-col justify-between">
               <div className="w-12 h-12 bg-[var(--color-background)] rounded-full flex items-center justify-center mb-6"><Download size={24}/></div>
               <div>
                  <h4 className="text-xl font-bold mb-2">
                       <VisualTextEditable 
                           value={mockData.labels.downloadsTitle} 
                           onSave={(v) => handleUpdate('labels.downloadsTitle', v)} 
                           isEditMode={!!isEditMode} 
                           as="span" 
                       />
                  </h4>
                  <p className="text-xs text-gray-400 font-medium mb-6">
                       <VisualTextEditable 
                           value={mockData.labels.downloadsSection} 
                           onSave={(v) => handleUpdate('labels.downloadsSection', v)} 
                           isEditMode={!!isEditMode} 
                           as="span" 
                       />
                  </p>
                  <div className="space-y-3">
                      {mockData.downloads.slice(0, 3).map((d, i) => (
                           <div key={i} className="flex justify-between items-center p-3 bg-[var(--color-background)] rounded-xl hover:bg-[var(--color-text)] hover:text-white transition-all cursor-pointer group">
                               <span className="text-[10px] font-bold truncate max-w-[120px]">
                                 <VisualTextEditable value={d.title} onSave={(v) => handleUpdate(`downloads[${i}].title`, v)} isEditMode={!!isEditMode} />
                               </span>
                               <span className="text-[9px] opacity-50 flex gap-1">
                                    <VisualTextEditable value={d.type} onSave={(v) => handleUpdate(`downloads[${i}].type`, v)} isEditMode={!!isEditMode} as="span" />
                                    <span>/</span>
                                    <VisualTextEditable value={d.size} onSave={(v) => handleUpdate(`downloads[${i}].size`, v)} isEditMode={!!isEditMode} as="span" />
                               </span>
                               {isEditMode && <button onClick={() => removeArrayItem('downloads', i)} className="text-[8px] text-red-500 font-bold ml-2 opacity-0 group-hover:opacity-100">X</button>}
                           </div>
                      ))}
                      {isEditMode && <button onClick={() => addArrayItem('downloads', { title: "NEW", size: "1.0 MB", type: "PDF" })} className="text-[8px] font-black uppercase text-[var(--color-primary)] underline">Sync File</button>}
                  </div>
               </div>
          </div>
          )}

          {/* Contacts Block */}
  {/* Contacts Block */}
          {mockData.sectionVisibility.contact && (
          <div id="contact" className="col-span-1 md:col-span-2 bg-[var(--color-secondary)] text-white rounded-[var(--radius)] p-10 flex flex-col md:flex-row gap-12 shadow-sm relative overflow-hidden group">
              <div className="relative z-10 w-full md:w-1/2">
                  <h4 className="text-[10px] font-bold tracking-[0.4em] uppercase opacity-40 mb-6">Access Coords</h4>
                  <div className="space-y-6">
                       <div className="flex gap-4">
                           <MapPin size={20} className="text-[var(--color-primary)] shrink-0" />
                           <p className="text-sm font-medium leading-relaxed opacity-80">
                             <VisualTextEditable value={mockData.contact.address} onSave={(v) => handleUpdate('contact.address', v)} isEditMode={!!isEditMode} multiline />
                           </p>
                       </div>
                       <div className="flex gap-4">
                           <Phone size={20} className="text-[var(--color-primary)] shrink-0" />
                           <p className="text-sm font-medium opacity-80">
                             <VisualTextEditable value={mockData.contact.phone} onSave={(v) => handleUpdate('contact.phone', v)} isEditMode={!!isEditMode} />
                           </p>
                       </div>
                  </div>
              </div>
              <div className="relative z-10 w-full md:w-1/2 flex flex-col justify-end items-start md:items-end">
                  <div className="flex gap-4 mb-4">
                      {[Facebook, Instagram, Youtube, Linkedin].map((Icon, i) => (
                          <a key={i} href="#" className="w-10 h-10 border border-white/10 rounded-xl flex items-center justify-center hover:bg-white hover:text-black transition-all">
                              <Icon size={18} />
                          </a>
                      ))}
                  </div>
                  <p className="text-[9px] font-medium opacity-40">&copy; {new Date().getFullYear()} {schoolName} // NODE_ALPHA</p>
              </div>
              {/* Background Glow */}
              <div className="absolute -bottom-20 -right-20 w-64 h-64 bg-[var(--color-primary)]/20 rounded-full blur-[100px]" />
          </div>
          )}

      </div>

      {/* Mobile Menu */}
      {menuOpen && (
          <div className="fixed inset-0 z-[100] bg-[var(--color-background)] p-8 animate-in fade-in slide-in-from-bottom duration-500 flex flex-col justify-between">
              <div className="flex justify-between items-center mb-12">
                  <span className="font-extrabold text-2xl tracking-tighter uppercase">{schoolName}</span>
                  <button onClick={() => setMenuOpen(false)}><X size={32} /></button>
              </div>
              <div className="flex flex-col gap-10">
                  {mockData.nav.filter(n => n.visible).map((n, i) => (
                      <a key={i} href={n.href} onClick={() => setMenuOpen(false)} className="text-4xl font-black uppercase italic tracking-tighter text-gray-400 hover:text-[var(--color-primary)] transition-all">
                          {n.label}
                      </a>
                  ))}
              </div>
              <Button className="w-full bg-[var(--color-primary)] text-white h-20 rounded-3xl text-xs font-bold uppercase tracking-widest mt-20">Initialize Portal</Button>
          </div>
      )}
    </div>
  );
};

export default CompactBento;
